import React, { useCallback, useEffect } from 'react';
import {
  CareType,
  ContractCareTypeScope,
  GetContractCareTypeUtilizationQuery,
  useGetContractCareTypeUtilizationLazyQuery,
} from '../../../graphQL';
import { LoadingPage } from '../../../Components/LoadingOverlay';
import { UnexpectedError } from '../../Shared';
import { ContractCareTypeUtilization } from './ContractCareTypeUtilization';
import { ContractUtilizationDashboard } from './ContractUtilizationDashboard';

type ContractCareTypeUtilizationContentProps = {
  contractId: number;
  careType: CareType;
  careTypeScopes: readonly ContractCareTypeScope[];
  cachedUtilizationContent?: ContractCareTypeUtilizationContentData;
  onUtilizationContentLoaded: (
    careType: CareType,
    content: ContractCareTypeUtilizationContentData
  ) => void;
  editable: boolean;
};

type ContractCareTypeUtilizationContentData = Extract<
  GetContractCareTypeUtilizationQuery['getContractCareTypeUtilization'],
  { __typename: 'ContractCareTypeUtilizationContent' }
>;

const getContractCareTypeScope = (
  careTypeScopes: readonly ContractCareTypeScope[],
  careType: CareType
): ContractCareTypeScope => {
  return (
    careTypeScopes.find(careTypeScope => careTypeScope.careType === careType) ?? {
      // Older contracts may not have an allocation row for every care type the
      // organization now supports. Match the backend's zero-allocation fallback.
      careType,
      sessions: 0,
      patients: 0,
    }
  );
};

export const ContractCareTypeUtilizationContent = ({
  contractId,
  careType,
  careTypeScopes,
  cachedUtilizationContent,
  onUtilizationContentLoaded,
  editable,
}: ContractCareTypeUtilizationContentProps) => {
  // ContractWeek ids are shared across care types, but their utilization values
  // are care type specific. Avoid Apollo normalization; ContractDashboard keeps
  // a small local cache keyed by care type for instant repeat tab visits.
  const [loadUtilizationContent, { data, loading, error }] =
    useGetContractCareTypeUtilizationLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const getLoadedUtilizationContent = useCallback(
    (queryData?: GetContractCareTypeUtilizationQuery) => {
      const content = queryData?.getContractCareTypeUtilization;

      return content?.__typename === 'ContractCareTypeUtilizationContent' &&
        content.careType === careType
        ? content
        : undefined;
    },
    [careType]
  );

  useEffect(() => {
    if (cachedUtilizationContent) {
      return;
    }

    loadUtilizationContent({ variables: { contractId, careType } });
  }, [cachedUtilizationContent, careType, contractId, loadUtilizationContent]);

  useEffect(() => {
    const loadedContent = getLoadedUtilizationContent(data);

    if (loadedContent) {
      onUtilizationContentLoaded(careType, loadedContent);
    }
  }, [careType, data, getLoadedUtilizationContent, onUtilizationContentLoaded]);

  const refetchUtilizationContent = async () => {
    await loadUtilizationContent({ variables: { contractId, careType } });
  };

  const loadedUtilizationContent = getLoadedUtilizationContent(data);
  const utilizationContent = cachedUtilizationContent ?? loadedUtilizationContent;

  if (loading && !utilizationContent) {
    return <LoadingPage />;
  }

  if (error) {
    return <UnexpectedError message={error.message} />;
  }

  const queryResult = data?.getContractCareTypeUtilization;

  if (!utilizationContent && queryResult?.__typename === 'GetContractError') {
    return <UnexpectedError message={queryResult.message} />;
  }

  if (!utilizationContent) {
    return <UnexpectedError message="Could not determine contract utilization data" />;
  }

  const scope = getContractCareTypeScope(careTypeScopes, careType);

  return (
    <>
      <ContractCareTypeUtilization
        careType={careType}
        scope={scope}
        utilization={utilizationContent.utilization}
      />
      <ContractUtilizationDashboard
        careType={careType}
        editable={editable}
        utilizationRecords={utilizationContent.weeks}
        refetch={refetchUtilizationContent}
      />
    </>
  );
};
